import { vehicleStyleToTypeMap } from '@/constants/vehicleDetails';

import type { IAiSearchFilters } from './aiSearchToParams';

const TYPE_NOUNS: Record<string, string> = {
  a: 'Class A motorhomes',
  b: 'camper vans',
  'camper-van': 'camper vans',
  c: 'Class C motorhomes',
  'truck-camper': 'truck campers',
  trailer: 'travel trailers',
  'fifth-wheel': 'fifth wheels',
  'folding-trailer': 'pop-up campers',
  'toy-hauler': 'toy haulers',
  'utility-trailer': 'utility trailers',
};

const VIBE_ADJECTIVES: Record<string, string> = {
  comfort: 'cozy',
  luxury: 'upscale',
  family: 'family-friendly',
  beginner: 'easy-to-drive',
  adventure: 'adventure-ready',
};

function sameSet(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every(x => b.includes(x));
}

function vehicleNoun(types: string[] | undefined): string {
  const unique = Array.from(new Set(types ?? []));
  if (unique.length === 0) return 'RVs';
  if (sameSet(unique, vehicleStyleToTypeMap.drivable)) return 'drivable RVs';
  if (sameSet(unique, vehicleStyleToTypeMap.towable)) return 'towable RVs';
  const nouns = Array.from(new Set(unique.map(t => TYPE_NOUNS[t]).filter(Boolean)));
  return nouns.length === 1 ? (nouns[0] as string) : 'RVs';
}

function formatDay(isoDate: string, locale: string): string | undefined {
  const date = new Date(`${isoDate}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return undefined;
  return new Intl.DateTimeFormat(locale, { month: 'long', day: 'numeric', timeZone: 'UTC' }).format(
    date,
  );
}

/**
 * Airbnb-style results heading for an AI search, e.g. "These cozy camper vans
 * near Asheville, NC can welcome you from November 6." Built from the search's
 * current filters (so it follows later facet changes) plus the vibes of the
 * last AI search, which aren't URL filters.
 *
 * @param filters - The current search, as recovered from the URL params.
 * @param vibes - Vibe slugs from the last AI search; the first one becomes the adjective.
 * @param locale - Locale for the date, e.g. "en-US".
 * @returns One sentence describing what the results show.
 */
export function describeAiSearchResults(
  filters: IAiSearchFilters,
  vibes: string[] = [],
  locale = 'en-US',
): string {
  const adjective = vibes.map(v => VIBE_ADJECTIVES[v]).find(Boolean);
  const subject = [adjective, vehicleNoun(filters.vehicleType)].filter(Boolean).join(' ');
  const place = filters.address ? ` near ${filters.address}` : '';
  const day = filters.dateFrom ? formatDay(filters.dateFrom, locale) : undefined;
  const predicate = day ? `can welcome you from ${day}` : 'match your trip';
  return `These ${subject}${place} ${predicate}.`;
}
